import type { MutableRefObject } from "react";
import type { OnboardingStep } from "@core/features/onboarding";
import {
  useUpdateActionStatus,
  ACTION_STATUS,
  ACTION_TYPE_SLUG,
} from "@core/features/actions";
import { ProfileFormContainer } from "@platform/features/programProfile";
import { QuizContainer } from "@platform/features/programQuiz";
import { SchedulingContainer } from "@platform/features/programScheduling";
import {
  TimezoneFormContainer,
  PhoneCollectionFormContainer,
} from "@platform/features/account";
import {
  ProfileStepContent,
  ScheduleStepContent,
} from "@platform/features/onboardingParticipant";
import {
  OrgDisplayNameStepContainer,
  OrgQuizStepContainer,
  OrgLogoStepContainer,
  OrgSocialsStepContainer,
} from "@platform/features/onboardingOrgAdmin";
import { Type } from "@themes/pollinator/ui/Type";
import type { StepValidationState } from "../../onboarding.types";
import { ErrorState } from "./ErrorState";
import { OnboardingStepLayout } from "./OnboardingStepLayout";
import { OnboardingInvitationStepContainer } from "../OnboardingInvitationStep";

export type Props = {
  /** The full onboarding step (includes action and sub_steps) */
  step: OnboardingStep;
  /** The program ID */
  programId: number;
  /** Callback when the step is completed */
  onComplete: () => void;
  /** Callback when the step is skipped (optional) */
  onSkip?: () => void;
  /** Whether to show action buttons in the step content (default: true) */
  showActionButtons?: boolean;
  /** Callback when step validation state changes (for parent footer) */
  onStepStateChange?: (state: StepValidationState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
};

/**
 * Renders the appropriate content for an onboarding step based on action type.
 * Routes to feature containers (ProfileFormContainer, QuizContainer, etc.)
 * and handles action status updates on completion.
 *
 * If the step has sub_steps defined, routes to multi-step content components
 * (ProfileStepContent, ScheduleStepContent) that handle internal wizard navigation.
 */
export function StepContent({
  step,
  programId,
  onComplete,
  onSkip,
  showActionButtons = true,
  onStepStateChange,
  submitRef,
}: Props) {
  const action = step.action;

  const { mutate: updateStatus } = useUpdateActionStatus({
    actionId: action?.id ?? 0,
    status: ACTION_STATUS.COMPLETED,
    currentStatus: action?.current_status?.name,
    onDone: async () => {
      onComplete();
    },
  });

  const handleComplete = () => {
    updateStatus();
  };

  if (!action) {
    return <ErrorState message="No action found for this step." />;
  }

  const slug = action.action_type?.slug;
  const hasSubSteps = step.sub_steps && step.sub_steps.length > 0;

  switch (slug) {
    case ACTION_TYPE_SLUG.PROFILE_PARTICIPANT:
      // Use multi-step content if sub_steps are defined
      if (hasSubSteps) {
        return (
          <ProfileStepContent
            action={action}
            subSteps={step.sub_steps}
            programId={programId}
            onComplete={handleComplete}
            onSkip={onSkip}
          />
        );
      }
      // Fallback to original single-screen form
      return (
        <ProfileFormContainer
          onSaveSuccess={handleComplete}
          showCancelButton={false}
          primaryButtonText="Save & Continue"
          showActionButtons={showActionButtons}
          onStepStateChange={onStepStateChange}
          submitRef={submitRef}
          className="w-full flex flex-col gap-6 p-6 max-w-3xl mx-auto"
        />
      );

    case ACTION_TYPE_SLUG.MATCHING_QUIZ: {
      const quizId =
        ((action.target_object_data as Record<string, unknown>)?.quiz as
          | number
          | undefined) ?? action.target_object_id;

      if (!quizId) {
        return <ErrorState message="Quiz not found for this action." />;
      }

      // Quiz passes through showActionButtons to control its internal StepActions
      // When showActionButtons=false, parent handles actions via submitRef
      return (
        <div className="w-full flex flex-col flex-1">
          <QuizContainer
            quizId={quizId}
            onCompleteQuiz={handleComplete}
            showActionButtons={showActionButtons}
            onStepStateChange={onStepStateChange}
            submitRef={submitRef}
          />
        </div>
      );
    }

    case ACTION_TYPE_SLUG.SCHEDULE: {
      // Use multi-step content if sub_steps are defined
      if (hasSubSteps) {
        return (
          <ScheduleStepContent
            action={action}
            subSteps={step.sub_steps}
            programId={programId}
            onComplete={handleComplete}
            onSkip={onSkip}
          />
        );
      }
      // Fallback to original single-screen scheduling
      // Extract display options for scheduling mode and configuration
      const displayOptions = action.display_options as Record<string, unknown> | undefined;
      const scheduledEngagementStartDate = displayOptions?.scheduled_engagement_start_date as string | undefined;
      const mode = displayOptions?.mode as string | undefined;
      const selectableOptions = displayOptions?.selectableOptions as unknown[] | undefined;
      const requiredSelections = displayOptions?.requiredSelections as number | undefined;
      const maxSelections = displayOptions?.maxSelections as number | undefined;

      const schedTitle = action.name || "Set Your Availability";
      const schedDescription =
        action.description || "Choose the times that work best for you.";
      return (
        <div className="w-full flex flex-col flex-1">
          <div className="flex flex-col gap-2">
            <Type variant="h2">{schedTitle}</Type>
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <div className="flex flex-col gap-4">
              <Type
                variant="body1"
                className="text-brand-700 font-medium"
              >
                {schedDescription}
              </Type>
              <SchedulingContainer
                programId={programId}
                scheduledEngagementStartDate={scheduledEngagementStartDate}
                mode={mode as "freeform" | "select-options" | "repeating-dropdown" | undefined}
                selectableOptions={selectableOptions as Parameters<typeof SchedulingContainer>[0]["selectableOptions"]}
                requiredSelections={requiredSelections}
                maxSelections={maxSelections}
                onSuccess={handleComplete}
                onSkip={onSkip}
                variant="embedded"
                primaryButtonText="Save & Continue"
                onStepStateChange={onStepStateChange}
                submitRef={submitRef}
              />
            </div>
          </div>
        </div>
      );
    }

    case ACTION_TYPE_SLUG.SEND_INVITATION: {
      return (
        <OnboardingInvitationStepContainer
          action={action}
          onComplete={handleComplete}
          onSkip={onSkip}
          showActionButtons={showActionButtons}
          onStepStateChange={onStepStateChange}
          submitRef={submitRef}
        />
      );
    }

    case ACTION_TYPE_SLUG.UPDATE_TIMEZONE: {
      const tzTitle = action.name || "Select Timezone";
      const tzDescription =
        action.description ||
        "Select the timezone where you will be in May.";
      return (
        <div className="w-full flex flex-col flex-1">
          <div className="flex flex-col gap-2">
            <Type variant="h2">{tzTitle}</Type>
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <div className="flex flex-col gap-4">
              <Type
                variant="body1"
                className="text-brand-700 font-medium"
              >
                {tzDescription}
              </Type>
              <TimezoneFormContainer
                onSaveSuccess={handleComplete}
                showCancelButton={false}
                primaryButtonText="Save & Continue"
                showActionButtons={showActionButtons}
                onStepStateChange={onStepStateChange}
                submitRef={submitRef}
                size="lg"
              />
            </div>
          </div>
        </div>
      );
    }

    case ACTION_TYPE_SLUG.COLLECT_PHONE_NUMBER: {
      return (
        <OnboardingStepLayout
          action={action}
          defaultTitle="Add Your Phone Number"
          defaultDescription="Stay connected with important program updates via text message (optional)."
          onComplete={handleComplete}
          onSkip={onSkip}
          showActionButtons={showActionButtons}
        >
          <PhoneCollectionFormContainer
            onSaveSuccess={handleComplete}
            onSkip={onSkip}
            primaryButtonText="Save & Continue"
            showActionButtons={showActionButtons}
            onStepStateChange={onStepStateChange}
            submitRef={submitRef}
          />
        </OnboardingStepLayout>
      );
    }

    case ACTION_TYPE_SLUG.ORG_DISPLAY_NAME: {
      const orgData = action.target_object_data as { id?: number } | null;
      const organizationId = orgData?.id;
      if (!organizationId) {
        return <ErrorState message="Organization not found for this action." />;
      }
      return (
        <OrgDisplayNameStepContainer
          programId={programId}
          organizationId={organizationId}
          onComplete={handleComplete}
          onSkip={onSkip}
          onStepStateChange={onStepStateChange}
          submitRef={submitRef}
        />
      );
    }

    case ACTION_TYPE_SLUG.ORG_ONBOARDING_QUIZ: {
      const orgData = action.target_object_data as { id?: number } | null;
      const organizationId = orgData?.id;
      if (!organizationId) {
        return <ErrorState message="Organization not found for this action." />;
      }
      return (
        <OrgQuizStepContainer
          programId={programId}
          organizationId={organizationId}
          action={action}
          onComplete={handleComplete}
          onSkip={onSkip}
          showActionButtons={showActionButtons}
          onStepStateChange={onStepStateChange}
          submitRef={submitRef}
        />
      );
    }

    case ACTION_TYPE_SLUG.ORG_UPLOAD_LOGO: {
      const orgData = action.target_object_data as { id?: number } | null;
      const organizationId = orgData?.id;
      if (!organizationId) {
        return <ErrorState message="Organization not found for this action." />;
      }
      return (
        <OrgLogoStepContainer
          programId={programId}
          organizationId={organizationId}
          onComplete={handleComplete}
          onSkip={onSkip}
          onStepStateChange={onStepStateChange}
          submitRef={submitRef}
        />
      );
    }

    case ACTION_TYPE_SLUG.ORG_UPDATE_SOCIALS: {
      const orgData = action.target_object_data as { id?: number } | null;
      const organizationId = orgData?.id;
      if (!organizationId) {
        return <ErrorState message="Organization not found for this action." />;
      }
      return (
        <OrgSocialsStepContainer
          programId={programId}
          organizationId={organizationId}
          onComplete={handleComplete}
          onSkip={onSkip}
          onStepStateChange={onStepStateChange}
          submitRef={submitRef}
        />
      );
    }

    default:
      return <ErrorState message={`Unknown step type: ${slug}`} />;
  }
}

export default StepContent;

